import React, { useState } from 'react';
import { Modal, Box, Typography, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import TimeField from './TimeField';
import CustomIconButton from './CustomIconButton';

interface RequestedVideoModalProps {
    open: boolean;
    onClose: () => void;
    handleRequested: (data: any) => void;
    title?: string;
    description?: string;
}

const formatDate = (date: string | null) => {
    if (!date) return '';

    const parsedDate = dayjs(date, 'DD/MM/YYYY');

    if (!parsedDate.isValid()) return '';

    return parsedDate.format('YYYY-MM-DD');
};

const formatTime = (time: string): string => {
    if (!time) return "";

    const lowerTime = time.trim().toLowerCase();

    const timeMatch = lowerTime.match(/^(\d{1,2}):?(\d{2})?\s*(am|pm)?$/);

    if (!timeMatch) return "";

    let [_, hoursStr, minutesStr, meridian] = timeMatch;

    let hours = parseInt(hoursStr, 10);
    const minutes = minutesStr ? parseInt(minutesStr, 10) : 0;

    if (isNaN(hours) || hours < 0 || hours > 12) return "";

    if (meridian) {
        if (meridian === "pm" && hours !== 12) {
            hours += 12;
        } else if (meridian === "am" && hours === 12) {
            hours = 0;
        }
    }

    const formattedHours = hours.toString().padStart(2, "0");
    const formattedMinutes = minutes.toString().padStart(2, "0");

    return `${formattedHours}:${formattedMinutes}`;
};

interface DateFieldProps {
    label: string;
    name: string;
    value: string | null;
    onChange: (value: string | null) => void;
    isDisabled?: boolean;
}

const DateField: React.FC<DateFieldProps> = ({
    label,
    name,
    value,
    onChange,
    isDisabled = false,
}) => {
    const [isInvalid, setisInvalid] = useState(false)
    const min = dayjs('01/01/2020', 'DD/MM/YYYY');
    const max = dayjs();
    return (
        <div className="flex flex-col w-full">
            <Typography variant="body2" fontWeight={600} color="textPrimary">
                {label}
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                    value={value ? dayjs(value, 'DD/MM/YYYY') : null}
                    onChange={(newValue) => {
                        if (!newValue) {
                            onChange(null);
                            return;
                        }

                        const parsed = dayjs(newValue);
                        if (
                            parsed.isValid() &&
                            parsed.isAfter(min.subtract(1, 'day')) &&
                            parsed.isBefore(max.add(1, 'day'))
                        ) {
                            setisInvalid(false);
                            onChange(parsed.format('DD/MM/YYYY'));
                        } else {
                            setisInvalid(true);
                            onChange(null);
                        }
                    }}
                    format="DD/MM/YYYY"
                    disabled={isDisabled}

                    slotProps={{
                        textField: {
                            fullWidth: true,
                            variant: 'outlined',
                            error: isInvalid, // you can control this with a state
                            helperText: isInvalid ? "Date must be between 01/01/2020 and today" : '',
                        },
                    }}
                    sx={(theme) => ({
                        width: '100%',
                        marginTop: '8px',
                        '& .MuiOutlinedInput-root': {
                            padding: '12px 16px',
                            fontSize: '14px',
                            fontWeight: 400,
                            borderRadius: '8px',
                            borderWidth: '1px',
                            borderColor: theme.palette.action.selected,
                        },
                        '& .MuiInputBase-input': {
                            padding: '0px',
                            borderWidth: '0px',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                        '& .MuiSvgIcon-root': {
                            color: theme.palette.primary.main,
                        },
                    })}
                    className={`mt-2 w-full ${isDisabled ? 'bg-gray-200 cursor-not-allowed' : ''}`}
                />
            </LocalizationProvider>
        </div>
    );
};


const RequestVehicleHistoryModal: React.FC<RequestedVideoModalProps> = ({
    open,
    onClose,
    handleRequested,
    title = 'Vehicle Running History',
    description = 'This is the description of the requested video.',
}) => {
    const [tripDetailFormData, setTripDetailFormData] = useState({
        startDate: null as string | null,
        endDate: null as string | null,
        startTime: '',
        endTime: '',
    });

    // const [cameraType, setCameraType] = useState('FCW');
    const [error, setError] = useState('');

    const handleDateChange = (field: string, value: string | null) => {
        setTripDetailFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleTimeChange = (field: string, value: string) => {
        setTripDetailFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSave = () => {
        const { startDate, endDate, startTime, endTime } = tripDetailFormData;

        if (startDate && endDate) {
            const newStartDate = dayjs(startDate, 'DD/MM/YYYY');
            const newEndDate = dayjs(endDate, 'DD/MM/YYYY');

            if (startTime && endTime) {
                const startDateTime = dayjs(`${startDate} ${startTime}`, 'DD/MM/YYYY hh:mm A');
                const endDateTime = dayjs(`${endDate} ${endTime}`, 'DD/MM/YYYY hh:mm A');
                const now = dayjs().second(0).millisecond(0);

                if (newStartDate.isSame(newEndDate, 'day')) {
                    if (startDateTime.isSame(endDateTime)) {
                        setError('End time cannot be same as start time');
                        return;
                    }
                }

                if (startDateTime.isAfter(endDateTime)) {
                    setError('End time cannot be before start time');
                    return;
                }

                if (endDateTime.isAfter(now)) {
                    setError('End time cannot be in the future');
                    return;
                }
            }
        }

        const formattedStartDate = formatDate(startDate);
        const formattedEndDate = formatDate(endDate);
        const formattedStartTime = formatTime(startTime);
        const formattedEndTime = formatTime(endTime);

        setError('');

        handleRequested({
            startDate: formattedStartDate,
            endDate: formattedEndDate,
            startTime: formattedStartTime,
            endTime: formattedEndTime,
            // cameraType,
        });

        onClose();
    };

    return (
        <Modal open={open} onClose={onClose} aria-labelledby="requested-video-title" aria-describedby="requested-video-description">
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '60%',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: 24,
                    p: 4,
                }}
            >
                <div className="flex flex-col gap-6">
                    <div className="flex flex-col gap-1">
                        <Typography variant="h6">{title}</Typography>
                        <Typography id="button" fontWeight={400} sx={{ mt: 1 }}>
                            {description}
                        </Typography>
                    </div>

                    {/* Date Fields */}
                    <div className="flex gap-4 flex-wrap">
                        <div className="flex-1 min-w-[200px]">
                            <DateField label="Start Date" name="startDate" value={tripDetailFormData.startDate} onChange={(date) => handleDateChange('startDate', date)} />
                        </div>
                        <div className="flex-1 min-w-[200px]">
                            <DateField label="End Date" name="endDate" value={tripDetailFormData.endDate} onChange={(date) => handleDateChange('endDate', date)} />
                        </div>
                    </div>

                    {/* Time Fields */}
                    <div className="flex gap-4 flex-wrap">
                        <div className="flex-1 min-w-[200px]">
                            <TimeField label="Start Time" minutesStep={1} name="startTime" value={tripDetailFormData.startTime} onChange={(time) => handleTimeChange('startTime', time || '')} />
                        </div>
                        <div className="flex-1 min-w-[200px]">
                            <TimeField label="End Time" name="endTime" minutesStep={1} value={tripDetailFormData.endTime} onChange={(time) => handleTimeChange('endTime', time || '')} />
                        </div>
                    </div>
                    {error && (
                        <Typography color="error" variant="body2">
                            {error}
                        </Typography>
                    )}
                </div>

                <div className="flex gap-2 mt-4 items-center justify-between">
                    <CustomIconButton text="Cancel" background="transparent" outline padding="0px" variant="outlined" onClick={onClose} />
                    <CustomIconButton text="Save" outline padding="0px" variant="contained" onClick={handleSave} />
                </div>
            </Box>
        </Modal>
    );
};

export default RequestVehicleHistoryModal;